import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";
import { API } from "@/utils/constants";
import { initializeOneSignal, loginOneSignal, logoutOneSignal, promptForNotifications } from "@/services/OneSignalConfig";
import { logger } from "../utils/logger";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Check for pending simulator session and save it to backend
async function savePendingSimSession() {
  try {
    const pending = localStorage.getItem('ff_pending_session');
    if (!pending) return;
    const session = JSON.parse(pending);
    if (!session?.session_id) return;
    await axios.post(`${API}/sim/sessions`, session);
    localStorage.removeItem('ff_pending_session');
  } catch (err) {
    logger.error('Failed to save pending simulator session:', err);
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize OneSignal once
  useEffect(() => {
    initializeOneSignal();
  }, []);

  // On mount, check if user has a valid session via httpOnly cookie
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(res => {
        setUser(res.data);
        if (res.data?.id) loginOneSignal(res.data.id);
        if (res.data?.notifications_enabled) promptForNotifications();
        savePendingSimSession();
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setUser(res.data.user);
    if (res.data.user?.id) loginOneSignal(res.data.user.id);
    savePendingSimSession();
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    setUser(res.data.user);
    if (res.data.user?.id) loginOneSignal(res.data.user.id);
    savePendingSimSession();
    return res.data;
  };

  const setAuthUser = useCallback((newUser) => {
    setUser(newUser);
    if (newUser?.id) loginOneSignal(newUser.id);
    savePendingSimSession();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Cookie may already be expired, continue with frontend cleanup
    }
    setUser(null);
    logoutOneSignal();
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, setAuthUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
